import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@repo/ui';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';

interface TopBarProps {
  onToggleSidebar: () => void;
  sidebarCollapsed: boolean;
}

export function TopBar({ onToggleSidebar, sidebarCollapsed }: TopBarProps) {
  const navigate = useNavigate();
  const { setTheme, theme } = useDarkMode();
  const [themeMenuOpen, setThemeMenuOpen] = React.useState(false);
  const [notificationOpen, setNotificationOpen] = React.useState(false);

  const themes = [
    { value: 'light', label: 'Light', icon: 'HiSun' },
    { value: 'dark', label: 'Dark', icon: 'HiMoon' },
    { value: 'system', label: 'System', icon: 'HiDesktopComputer' },
  ] as const;

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    setThemeMenuOpen(false);
  };

  // Get current theme icon
  const currentThemeIcon = themes.find(t => t.value === theme)?.icon || 'HiSun';

  return (
    <header
      className={`
        fixed top-0 right-0 z-50 h-16
        bg-xtnd-white/80 dark:bg-xtnd-dark/80 backdrop-blur-lg
        border-b border-xtnd-light dark:border-xtnd-dark-700
        transition-all duration-300
        ${sidebarCollapsed ? 'left-20' : 'left-72'}
      `}
    >
      <div className="h-full flex items-center justify-between px-6">
        {/* Left: Toggle Sidebar Button + Project Name */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="
              group p-2.5 rounded-xl
              bg-xtnd-light dark:bg-xtnd-dark-700
              hover:bg-xtnd-blue dark:hover:bg-xtnd-blue
              transition-all duration-200
              shadow-sm hover:shadow-md
            "
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon
              name="HiMenu"
              size="md"
              className="text-xtnd-dark dark:text-xtnd-white group-hover:text-xtnd-white transition-colors"
            />
          </button>

          {/* Project Name */}
          <div className="flex flex-col">
            <span className="text-lg font-bold text-xtnd-dark dark:text-xtnd-white leading-tight">
              XTND Juniper
            </span>
            <span className="text-xs text-xtnd-dark-400 dark:text-xtnd-light">
              Legal Invoice Automation
            </span>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationOpen(!notificationOpen)}
              className="
                relative p-2.5 rounded-xl
                bg-xtnd-light dark:bg-xtnd-dark-700
                hover:bg-xtnd-light-600 dark:hover:bg-xtnd-dark-600
                transition-all duration-200
              "
              aria-label="Notifications"
              title="Notifications"
            >
              <Icon name="HiBell" size="md" className="text-xtnd-dark dark:text-xtnd-white" />
              {/* Notification Badge */}
              <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full ring-2 ring-xtnd-white dark:ring-xtnd-dark" />
            </button>

            {/* Notification Dropdown */}
            {notificationOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setNotificationOpen(false)}
                  onKeyDown={e => e.key === 'Escape' && setNotificationOpen(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close notifications"
                />
                <div className="absolute right-0 mt-2 w-80 bg-xtnd-white dark:bg-xtnd-dark rounded-xl shadow-xl border border-xtnd-light dark:border-xtnd-dark-700 z-20">
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-xtnd-dark dark:text-xtnd-white mb-3">
                      Notifications
                    </h3>
                    <div className="space-y-2">
                      <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-light text-center py-8">
                        No new notifications
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Settings */}
          <button
            className="
              p-2.5 rounded-xl
              bg-xtnd-light dark:bg-xtnd-dark-700
              hover:bg-xtnd-light-600 dark:hover:bg-xtnd-dark-600
              transition-all duration-200
            "
            aria-label="Settings"
            title="Settings"
            onClick={() => navigate('/settings')}
          >
            <Icon name="HiCog" size="md" className="text-xtnd-dark dark:text-xtnd-white" />
          </button>

          {/* Theme Switcher */}
          <div className="relative">
            <button
              onClick={() => setThemeMenuOpen(!themeMenuOpen)}
              className="
                p-2.5 rounded-xl
                bg-xtnd-light dark:bg-xtnd-dark-700
                hover:bg-xtnd-light-600 dark:hover:bg-xtnd-dark-600
                transition-all duration-200
              "
              aria-label="Change theme"
              title="Change theme"
            >
              <Icon
                name={currentThemeIcon}
                size="md"
                className="text-xtnd-dark dark:text-xtnd-white"
              />
            </button>

            {/* Theme Menu Dropdown */}
            {themeMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setThemeMenuOpen(false)}
                  onKeyDown={e => e.key === 'Escape' && setThemeMenuOpen(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close theme menu"
                />
                <div className="absolute right-0 mt-2 w-48 bg-xtnd-white dark:bg-xtnd-dark rounded-xl shadow-xl border border-xtnd-light dark:border-xtnd-dark-700 z-20 overflow-hidden">
                  <div className="p-2">
                    {themes.map(themeOption => (
                      <button
                        key={themeOption.value}
                        onClick={() => handleThemeChange(themeOption.value)}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                          text-sm font-medium transition-all
                          ${
                            theme === themeOption.value
                              ? 'bg-xtnd-blue text-xtnd-white'
                              : 'text-xtnd-dark dark:text-xtnd-white hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700'
                          }
                        `}
                      >
                        <Icon name={themeOption.icon} size="sm" />
                        <span>{themeOption.label}</span>
                        {theme === themeOption.value && (
                          <Icon name="HiCheck" size="sm" className="ml-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
